import { faCirclePlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React from "react";
import { Button, Card, Col, Row } from "react-bootstrap";

import { CountryWithCode } from "../../../../config/filingConstants";
import { IFormITR1 } from "../../../../config/interfaces";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import { FormField, SelectInputField, TextInputField } from "../../../fields";
import PreventEditComponent from "../../../PreventEditComponent";

const NRIStatusForm: React.FC = () => {
  const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<IFormITR1>();

  return (
    <>
      <Card className="more-info-card shadow-none">
        <Card.Header>
          <Card.Title>
            <div>
              <strong>NRI</strong>&nbsp;Enter your duration of stay in India
            </div>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <section className="mb-5">
            <FormField
              label="Days stayed between 1 Apr 2022 to 31 Mar 2023 (in days)"
              isRequired={true}
              sm={6}
            >
              <TextInputField
                name="NRIStayInIndia1"
                value={values.NRIStayInIndia1}
                onBlur={handleBlur("NRIStayInIndia1")}
                onChange={(value) => setFieldValue("NRIStayInIndia1", value)}
                isInvalid={Boolean(errors.NRIStayInIndia1) && touched.NRIStayInIndia1}
                errorMsg={errors.NRIStayInIndia1}
              />
            </FormField>
            <FormField
              label="Days stayed between 1 Apr 2018 to 31 Mar 2022 (in days)"
              isRequired={true}
              sm={6}
            >
              <TextInputField
                name="NRIStayInIndia2"
                value={values.NRIStayInIndia2}
                onBlur={handleBlur("NRIStayInIndia2")}
                onChange={(value) => setFieldValue("NRIStayInIndia2", value)}
                isInvalid={Boolean(errors.NRIStayInIndia2) && touched.NRIStayInIndia2}
                errorMsg={errors.NRIStayInIndia2}
              />
            </FormField>
          </section>

          <FieldArray name="NRIJurisdictionDetails">
            {({ push, remove, form }) => (
              <>
                {values?.NRIJurisdictionDetails?.length > 0
                  ? values?.NRIJurisdictionDetails?.map((_, index) => (
                      <section className="mb-3" key={`NRI-${index}`}>
                        <NRIForm
                          index={index}
                          form={form}
                          onRemove={(itemIdx: number) => remove(itemIdx)}
                        />
                      </section>
                    ))
                  : null}
                <PreventEditComponent>
                  <Button variant="link" className="btn-add-link" onClick={() => push(["", ""])}>
                    <FASvgIcon icon={faCirclePlus} />
                    Add more
                  </Button>
                </PreventEditComponent>
              </>
            )}
          </FieldArray>
        </Card.Body>
      </Card>
    </>
  );
};

export default NRIStatusForm;

type NRIFormProps = {
  index: number;
  form: FormikProps<IFormITR1>;
  onRemove: (itemIdx: number) => void;
};

const NRIForm: React.FC<NRIFormProps> = (props: NRIFormProps) => {
  const { index, onRemove, form } = props;

  const { setFieldValue, handleBlur, values, touched, errors } = form;

  return (
    <>
      <FormField
        label={
          <>
            Jurisdiction of Resident
            <small>
              Jurisdiction of Residence is your place of stay outside India. Select the Country of
              your stay.
            </small>
          </>
        }
        isRequired={false}
        sm={6}
      >
        <SelectInputField
          placeholder="Select Country"
          name={`NRIJurisdictionDetails.${index}.[0]`}
          menuZIndex={9}
          isSearchable={true}
          isOptionArrayOfObject={true}
          options={UtilService.convertToArray(CountryWithCode)}
          value={{
            "label": CountryWithCode[values.NRIJurisdictionDetails[index]?.[0]],
            "value": values.NRIJurisdictionDetails[index]?.[0]
          }}
          onChange={(selectedOption) => {
            if (selectedOption) {
              setFieldValue(`NRIJurisdictionDetails.${index}.[0]`, selectedOption.value);
            } else {
              setFieldValue(`NRIJurisdictionDetails.${index}.[0]`, undefined);
            }
          }}
          onBlur={handleBlur(`NRIJurisdictionDetails.${index}.[0]`)}
          isInvalid={
            Boolean(getIn(errors, `NRIJurisdictionDetails.${index}.[0]`)) &&
            getIn(touched, `NRIJurisdictionDetails.${index}.[0]`)
          }
          errorMsg={getIn(errors, `NRIJurisdictionDetails.${index}.[0]`)}
        />
      </FormField>

      <FormField
        label={
          <>
            Tax Identification number
            <small>
              Tax Identification Number (TIN) is your ID number. E.g. If you live in USA, SSN number
              is your TIN.
            </small>
            <small>If you don't have a TIN, just enter your Passport Number.</small>
          </>
        }
        isRequired={false}
        sm={6}
      >
        <TextInputField
          name={`NRIJurisdictionDetails.${index}.[1]`}
          value={values.NRIJurisdictionDetails[index]?.[1] || ""}
          onBlur={handleBlur(`NRIJurisdictionDetails.${index}.[1]`)}
          onChange={(value) => setFieldValue(`NRIJurisdictionDetails.${index}.[1]`, value)}
          isInvalid={
            Boolean(getIn(errors, `NRIJurisdictionDetails.${index}.[1]`)) &&
            getIn(touched, `NRIJurisdictionDetails.${index}.[1]`)
          }
          errorMsg={getIn(errors, `NRIJurisdictionDetails.${index}.[1]`)}
        />
      </FormField>

      {index > 0 && (
        <PreventEditComponent>
          <Row>
            <Col>
              <Button
                variant="transparent"
                className="btn-delete-full"
                onClick={() => onRemove(index)}
              >
                <FASvgIcon icon={faTrash} />
                <span>Delete</span>
              </Button>
            </Col>
          </Row>
        </PreventEditComponent>
      )}
      {index !== values.NRIJurisdictionDetails.length - 1 && <hr className="my-4" />}
    </>
  );
};
